import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  model,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import { SiaBaseScriptComponent, SiaSizeDetectorService } from '@sia/shared';
import { Guid } from 'guid-typescript';

@Component({
  selector: 'sia-base-editor',
  template: '',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiaBaseEditorSignalComponent
  extends SiaBaseScriptComponent
  implements OnDestroy
{
  protected cdr = inject(ChangeDetectorRef);
  protected siaSizeDetector = inject(SiaSizeDetectorService);

  infoIcon = signal('&#8505');

  //#endregion
  valueChanged = output<any>();
  //#endregion

  // 1. Migrate Inputs
  key = input<string | undefined | null>();

  // Migration note: merged separate _status field and setter into signal input
  status = model<
    'error' | 'warning' | 'info' | 'modified' | null | undefined
  >();

  // Migration note: merged _modified field and setter into signal input
  modified = input(false);

  name = input<string>();

  // Migration note: merged _showBottomMargin field and setter.
  // cdr.detectChanges() removed as signal updates notify change detection automatically in Zoneless/OnPush.
  showBottomMargin = input(false);

  tabDisabled = input<boolean>();

  // Migration note: merged _id and setter. Initial value kept.
  id = input<string | undefined>(Guid.create().toString());

  statusMessage = input<string>();
  statusMessages = model<Map<string, string> | undefined>();

  customClass = input<string | undefined>('');

  // Migration note: inputClass was both an input and updated internally.
  // Modeled here as a computed signal driven by status/modified/label logic.
  // The original @Input() inputClass binding would be ignored by the logic so it is omitted to favor the computed derivation.

  // Migration note: disabled setter had type coercion logic.
  disabled = input(undefined, {
    transform: (v: boolean | string | undefined | null) => {
      if (v === undefined || v === null) return undefined;
      return v;
    },
  });

  // Value input with side effects handled in effect()
  value = model<any>();

  label = input<string | undefined | null>();
  info = input<string | undefined | null>();

  // Internal State
  // class property was updated by disabled setter and focus events
  class = signal('sia-not-focus');

  // Computed class logic from setInputClass
  inputClass = computed(() => {
    const baseClass = 'sia-input sia-input';
    let inputFinalClass = '';

    const currentStatus = this.status();
    this.modified();
    this.label();
    if (currentStatus) {
      if (currentStatus == 'error') {
        inputFinalClass += baseClass + '--invalid ';
      } else {
        inputFinalClass += baseClass + '--' + currentStatus;
      }
    } else {
      if (this.modified()) {
        inputFinalClass += baseClass + '--modified ';
      }
    }
    if (!this.label()) {
      inputFinalClass += baseClass + '--no-label ';
    }
    // Note: The original logic ended here, assigning to this.inputClass
    return inputFinalClass;
  });

  // Computed disabled state
  isDisabled = computed(() => {
    const v = this.disabled();
    return typeof v === 'boolean' ? v : v === 'true';
  });

  constructor() {
    super();

    // Effect to handle disabled class toggle
    effect(() => {
      const v = this.disabled();
      if (v === undefined || v === null) return;

      // logic from original set disabled(v)
      if (v) {
        this.class.set('sia-disabled');
      } else {
        this.class.set('sia-not-focus');
      }
    });

    // Effect for value changes (debounce and emit)
    let valueInitialized = false;
    effect(
      (onCleanup) => {
        const v = this.value();

        // Allow derived components to react immediately to internal value changes
        // If it returns a value different from v, we update the model.
        // We must avoid loops so we check equality.
        const transformed = this.onInternalValueSet(v);

        if (transformed !== undefined && transformed !== v) {
          // Update the model with the transformed value
          // use { allowSignalWrites: true } is implicit in effect usually? No, strictly forbids unless allowed.
          // We need to allow it.
          this.value.set(transformed);
          // The effect will re-run with the new value.
          return;
        }

        // Avoid emitting on the very first initialization read
        if (!valueInitialized) {
          valueInitialized = true;
          return;
        }
        const timer = setTimeout(() => {
          // Emit both legacy and two-way binding outputs
          this.valueChanged.emit(v);
        }, 300);

        onCleanup(() => {
          clearTimeout(timer);
        });
      },
      { allowSignalWrites: true },
    );
  }

  //#region public

  /**
   * Hook for derived classes to react to value assignment (immediately, before debounced outputs fire).
   * Can return a new value to transform the input (normalizer).
   * If it returns undefined, no transformation is applied.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onInternalValueSet(_newValue: any): any | void {}

  isFocused(e: any) {
    this.class.set(' sia-focus');
  }
  isNotFocused(e: any) {
    this.class.set(' sia-not-focus');
  }

  fieldErrors = computed(() => {
    const messages = this.statusMessages();
    if (messages && messages.size > 0) {
      let errors = '';
      for (let value of messages.values()) {
        errors += value + '\n';
      }
      return errors;
    }
    return this.statusMessage() ?? '';
  });

  visibleTooltip = computed(() => {
    const messages = this.statusMessages();
    return (messages && messages.size > 1) ?? false;
  });
  //#endregion

  @HostListener('window:unload')
  override ngOnDestroy() {
    // Signals automatically clean up, but explicit cleanup from original follows:
    // this.statusMessages?.clear(); // statusMessages is now a read-only input signal, cannot clear.
    // this._value = null; // value is read-only input.
    // this.key = ''; // key is read-only input.
    // this.label = ''; // label is read-only input.

    // Timeout is cleared by effect cleanup automatically.

    super.ngOnDestroy();
  }
}
